import React, { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { OUTCOMES, EMPTY_EPISODE_FORM, inputCls, OutcomeBadge, Field } from "./_shared";

export default function EpisodesAdmin({ episodes, apps, reload, publishSource, setPublishSource }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => { if (publishSource) { setEditing(null); setOpen(true); } }, [publishSource]);

  const openNew = () => { setPublishSource(null); setEditing(null); setOpen(true); };
  const openEdit = (ep) => { setPublishSource(null); setEditing(ep); setOpen(true); };
  const closeForm = (o) => { setOpen(o); if (!o) { setPublishSource(null); setEditing(null); } };

  return (
    <>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Episodes</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter">{episodes.length} published</h1>
        </div>
        <button data-testid="new-episode-btn" onClick={openNew} className="px-6 py-3 bg-lime text-black font-heading font-bold uppercase tracking-wider hover:scale-[1.03] transition-transform">
          + New episode
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="episodes-admin-grid">
        {episodes.map((ep) => (
          <div key={ep.id} className="bg-surface border border-white/10 flex flex-col">
            <div className="aspect-video bg-black relative overflow-hidden">
              {ep.cover_image_path ? <img src={fileUrl(ep.cover_image_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-heading text-3xl font-black uppercase">{ep.startup_name?.slice(0, 3)}</div>}
              <div className="absolute top-2 left-2"><OutcomeBadge o={ep.outcome} /></div>
            </div>
            <div className="p-4">
              <div className="font-heading text-lg font-black uppercase truncate">{ep.startup_name}</div>
              <div className="text-white/50 text-xs mt-1 line-clamp-2">{ep.one_liner}</div>
              <div className="mt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/40">
                <span>{ep.industry || "—"}</span>
                <a href={`/episodes/${ep.slug}`} target="_blank" rel="noreferrer" className="hover:text-lime">View →</a>
              </div>
              <button
                data-testid={`edit-episode-${ep.id}`}
                onClick={() => openEdit(ep)}
                className="mt-3 w-full py-2 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-lime hover:text-lime transition-colors"
              >
                Edit episode
              </button>
              <button
                data-testid={`delete-episode-${ep.id}`}
                onClick={async () => {
                  if (!window.confirm(`Delete episode "${ep.startup_name}"? This cannot be undone.`)) return;
                  try {
                    await api.delete(`/admin/episodes/${ep.id}`);
                    toast.success("Episode deleted");
                    reload();
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
                  }
                }}
                className="mt-2 w-full py-2 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-danger hover:text-danger transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {episodes.length === 0 && (
          <div className="col-span-full border border-dashed border-white/10 p-16 text-center text-white/50">No episodes yet. Publish your first one.</div>
        )}
      </div>
      <EpisodeForm open={open} onOpenChange={closeForm} source={publishSource} editing={editing} onSaved={reload} apps={apps} />
    </>
  );
}

function EpisodeForm({ open, onOpenChange, source, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY_EPISODE_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(editing);
  const editingId = editing?.id || null;
  const sourceId = source?.id || null;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        startup_name: editing.startup_name || "",
        one_liner: editing.one_liner || "",
        long_description: editing.long_description || "",
        website: editing.website || "",
        country: editing.country || "",
        year_incorporation: editing.year_incorporation || new Date().getFullYear(),
        industry: editing.industry || "",
        outcome: editing.outcome || "invested",
        linkedin_video_url: editing.linkedin_video_url || "",
        cover_image_path: editing.cover_image_path || "",
        host_notes: editing.host_notes || "",
        application_id: editing.application_id || null,
        published: editing.published !== undefined ? editing.published : true,
        tag_along_total: editing.tag_along_total || 0,
        tag_along_enabled: editing.tag_along_enabled !== false,
        solo_invest_enabled: editing.solo_invest_enabled === true,
      });
    } else if (source) {
      setForm({
        ...EMPTY_EPISODE_FORM,
        startup_name: source.startup_name || "",
        one_liner: source.one_liner || "",
        long_description: source.long_description || "",
        website: source.website || "",
        country: source.country || "",
        year_incorporation: source.year_incorporation || new Date().getFullYear(),
        industry: source.industry || "",
        outcome: source.outcome || "invested",
        application_id: source.id || null,
      });
    } else {
      setForm(EMPTY_EPISODE_FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, sourceId, open]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, cover_image_path: data.path }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, tag_along_total: parseInt(form.tag_along_total) || 0, year_incorporation: parseInt(form.year_incorporation) || null };
      if (isEdit) {
        await api.patch(`/admin/episodes/${editing.id}`, payload);
        toast.success("Episode updated");
      } else {
        await api.post("/admin/episodes", payload);
        toast.success("Episode published");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="episode-form-modal" className="max-w-3xl bg-surface border-white/10 rounded-none text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-black uppercase tracking-tighter">{isEdit ? "Edit episode" : "Publish episode"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Startup name *"><Input data-testid="ep-startup-name" required value={form.startup_name} onChange={set("startup_name")} className={inputCls} /></Field>
            <Field label="Outcome *">
              <select data-testid="ep-outcome" value={form.outcome} onChange={set("outcome")} className={`w-full h-10 px-3 ${inputCls}`}>
                {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Website"><Input data-testid="ep-website" value={form.website} onChange={set("website")} className={inputCls} /></Field>
            <Field label="Industry"><Input data-testid="ep-industry" value={form.industry} onChange={set("industry")} className={inputCls} /></Field>
            <Field label="Country"><Input data-testid="ep-country" value={form.country} onChange={set("country")} className={inputCls} /></Field>
            <Field label="Year founded"><Input data-testid="ep-year" type="number" value={form.year_incorporation} onChange={set("year_incorporation")} className={inputCls} /></Field>
          </div>
          <Field label="One-liner *"><Input data-testid="ep-one-liner" required maxLength={280} value={form.one_liner} onChange={set("one_liner")} className={inputCls} /></Field>
          <Field label="LinkedIn video URL (or YouTube)"><Input data-testid="ep-video-url" value={form.linkedin_video_url} onChange={set("linkedin_video_url")} className={inputCls} placeholder="https://www.linkedin.com/…" /></Field>
          <Field label="Cover image">
            <div className="flex items-center gap-3">
              <input data-testid="ep-cover-input" type="file" accept="image/*" onChange={uploadCover} className="text-sm text-white/70 file:bg-lime file:text-black file:font-mono file:uppercase file:text-xs file:tracking-widest file:border-0 file:px-3 file:py-2 file:mr-3" />
              {uploading && <span className="text-xs font-mono text-white/50">Uploading…</span>}
              {form.cover_image_path && !uploading && (
                <img src={fileUrl(form.cover_image_path)} alt="cover" className="w-24 h-14 object-cover border border-white/10" />
              )}
            </div>
          </Field>
          <Field label="Long description"><Textarea data-testid="ep-long-desc" rows={4} value={form.long_description} onChange={set("long_description")} className={inputCls} /></Field>
          <Field label="Host's takeaways (public)"><Textarea data-testid="ep-host-notes" rows={4} value={form.host_notes} onChange={set("host_notes")} className={inputCls} /></Field>
          <Field label="Tag-along total (€) — investors who tagged along">
            <Input data-testid="ep-tag-along-total" type="number" min="0" step="500" value={form.tag_along_total} onChange={set("tag_along_total")} className={inputCls} placeholder="0" />
          </Field>
          <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
            <input
              data-testid="ep-tag-along-enabled"
              type="checkbox"
              checked={form.tag_along_enabled !== false}
              onChange={(e) => setForm((s) => ({ ...s, tag_along_enabled: e.target.checked }))}
              className="w-4 h-4 accent-lime"
            />
            <span>Tag-along rights offered on this episode</span>
          </label>
          <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
            <input
              data-testid="ep-solo-invest-enabled"
              type="checkbox"
              checked={form.solo_invest_enabled === true}
              onChange={(e) => setForm((s) => ({ ...s, solo_invest_enabled: e.target.checked }))}
              className="w-4 h-4 accent-lime"
            />
            <span>Allow "Invest anyway" interest (host passed / follow-up episodes)</span>
          </label>
          <div className="flex justify-end pt-2">
            <button data-testid="ep-submit" type="submit" disabled={saving} className="px-8 py-3 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.03] transition-transform disabled:opacity-50">
              {saving ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes →" : "Publish →")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
